import { useState, useEffect, useRef } from 'react';
import { LogOut } from 'lucide-react';
import TangoEyeLogo from '../../assets/MasterEyeLogo';
import { useAuth } from '../../auth/AuthContext';
import styles from './Sidebar.module.css';

function SettingsModal({ onClose }) {
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <svg width="80" height="80" viewBox="0 0 80 80" fill="none" style={{ margin: '0 auto', display: 'block' }}>
          <circle cx="40" cy="40" r="40" fill="#e6f6ff"/>
          <circle cx="40" cy="40" r="16" fill="none" stroke="#00A3FF" strokeWidth="3"/>
          <path d="M40 24v-6M40 62v-6M24 40h-6M62 40h-6" stroke="#00A3FF" strokeWidth="3" strokeLinecap="round"/>
          <path d="M29.4 29.4l-4.2-4.2M54.8 54.8l-4.2-4.2M50.6 29.4l4.2-4.2M25.2 54.8l4.2-4.2" stroke="#66c2ff" strokeWidth="2.5" strokeLinecap="round"/>
          <circle cx="40" cy="40" r="5" fill="#00A3FF"/>
        </svg>
        <h2>Settings</h2>
        <p>We're building something great here.<br />Settings will be available in the next release.</p>
        <button className={styles.closeBtn} onClick={onClose}>Got it</button>
      </div>
    </div>
  );
}

// Two-letter initials from the user's name, falling back to "A2".
function initials(name) {
  if (!name) return 'A2';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'A2';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function Sidebar() {
  const [showSettings, setShowSettings] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { profile, logout } = useAuth();
  const userRef = useRef(null);

  // Close the user menu on outside click.
  useEffect(() => {
    function handler(e) {
      if (userRef.current && !userRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      <aside className={styles.sidebar}>
        <div className={styles.logoSlot}>
          <TangoEyeLogo size={30} />
        </div>

        <nav className={styles.nav}>
          <button className={`${styles.item} ${styles.active}`} data-tip="Dashboard">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="3" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
          </button>
        </nav>

        <div className={styles.bottom}>
          <button className={styles.item} data-tip="Settings" onClick={() => setShowSettings(true)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
          <div className={styles.divider} />

          {/* User avatar → click for logout menu */}
          <div ref={userRef} className={styles.userWrap}>
            <button
              className={styles.avatar}
              onClick={() => setMenuOpen(o => !o)}
              title={profile?.name || 'Account'}
              aria-label="Account menu"
            >
              {initials(profile?.name)}
            </button>
            {menuOpen && (
              <div className={styles.userMenu}>
                {profile?.name && <div className={styles.userMenuName}>{profile.name}</div>}
                {profile?.email && <div className={styles.userMenuEmail}>{profile.email}</div>}
                <button className={styles.logoutItem} onClick={logout}>
                  <LogOut size={14} />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    </>
  );
}
